import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import StatusBadge from "@/components/base/StatusBadge";
import { formatFullDate, daysUntil } from "@/lib/format";
import { downloadCsv, type CsvColumn } from "@/lib/csv";
import { useLicenseLabels } from "@/hooks/useLicenseLabels";
import type { License } from "@/pages/licenses/hooks/useMyLicenses";

type BucketKey = "overdue" | "urgent" | "soon" | "later" | "perpetual";

interface BucketDef {
  key: BucketKey;
  labelKey: string;
  icon: string;
  chip: string;
}

const BUCKETS: BucketDef[] = [
  { key: "overdue", labelKey: "licenses.plannerOverdue", icon: "ri-error-warning-line", chip: "bg-accent-100 text-accent-800" },
  { key: "urgent", labelKey: "licenses.plannerUrgent", icon: "ri-alarm-warning-line", chip: "bg-accent-100 text-accent-700" },
  { key: "soon", labelKey: "licenses.plannerSoon", icon: "ri-timer-line", chip: "bg-secondary-100 text-secondary-900" },
  { key: "later", labelKey: "licenses.plannerLater", icon: "ri-calendar-line", chip: "bg-primary-100 text-primary-700" },
  { key: "perpetual", labelKey: "licenses.plannerPerpetual", icon: "ri-checkbox-circle-line", chip: "bg-background-200 text-foreground-700" },
];

function bucketOf(license: License): BucketKey {
  if (!license.expires_at) return "perpetual";
  const days = daysUntil(license.expires_at);
  if (days < 0) return "overdue";
  if (days <= 30) return "urgent";
  if (days <= 90) return "soon";
  return "later";
}

interface ExpiryPlannerProps {
  licenses: License[];
}

// 到期规划：按紧迫度把许可证分组，点任一分组即可展开该组的许可证，
// 让客户提前安排续期，避免授权中断。纯前端聚合，无需额外请求。
export default function ExpiryPlanner({ licenses }: ExpiryPlannerProps) {
  const { t } = useTranslation();
  const { edition, statusMeta } = useLicenseLabels();
  const [active, setActive] = useState<BucketKey | null>(null);
  const [exporting, setExporting] = useState(false);

  const grouped = useMemo(() => {
    const map: Record<BucketKey, License[]> = { overdue: [], urgent: [], soon: [], later: [], perpetual: [] };
    licenses.forEach((license) => map[bucketOf(license)].push(license));
    (Object.keys(map) as BucketKey[]).forEach((key) => {
      map[key].sort((a, b) => {
        if (key === "perpetual") return a.product_name.localeCompare(b.product_name);
        return daysUntil(a.expires_at as string) - daysUntil(b.expires_at as string);
      });
    });
    return map;
  }, [licenses]);

  const total = licenses.length;
  const activeList = active ? grouped[active] : [];
  const activeDef = active ? BUCKETS.find((bucket) => bucket.key === active) ?? null : null;

  // 待续期台账：把带到期时间（非永久）的许可证按紧迫度导出，方便客户按表续期。
  const renewable = useMemo(() => {
    const rows = licenses.filter((license) => Boolean(license.expires_at));
    return rows.sort((a, b) => daysUntil(a.expires_at as string) - daysUntil(b.expires_at as string));
  }, [licenses]);

  const handleExport = () => {
    if (renewable.length === 0 || exporting) return;
    const bucketLabel: Record<BucketKey, string> = {
      overdue: t("licenses.plannerOverdue"),
      urgent: t("licenses.plannerUrgent"),
      soon: t("licenses.plannerSoon"),
      later: t("licenses.plannerLater"),
      perpetual: t("licenses.plannerPerpetual"),
    };
    const columns: CsvColumn<License>[] = [
      { key: "product", label: t("licenses.plannerColProduct"), value: (row) => row.product_name },
      { key: "edition", label: t("licenses.plannerColEdition"), value: (row) => edition(row.edition) },
      { key: "key", label: t("licenses.plannerColKey"), value: (row) => `LH-****-${row.license_key.slice(-4)}` },
      {
        key: "expiry",
        label: t("licenses.plannerColExpiry"),
        value: (row) => (row.expires_at ? formatFullDate(row.expires_at) : t("licenses.permanent")),
      },
      {
        key: "days",
        label: t("licenses.plannerColDaysLeft"),
        value: (row) => (row.expires_at ? daysUntil(row.expires_at) : "-"),
      },
      { key: "status", label: t("licenses.plannerColStatus"), value: (row) => statusMeta(row.status).label },
      { key: "urgency", label: t("licenses.plannerColUrgency"), value: (row) => bucketLabel[bucketOf(row)] },
    ];
    const stamp = new Date().toISOString().slice(0, 10);
    downloadCsv(`${t("licenses.plannerExportFileName")}_${stamp}.csv`, columns, renewable);
    setExporting(true);
    window.setTimeout(() => setExporting(false), 2000);
  };

  return (
    <div className="mb-6 rounded-lg border border-background-200 bg-background-50 p-5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className="flex h-8 w-8 items-center justify-center rounded-md bg-primary-100 text-primary-700">
            <i className="ri-calendar-todo-line" />
          </span>
          <div>
            <h2 className="font-heading text-sm font-semibold text-foreground-950">{t("licenses.plannerTitle")}</h2>
            <p className="text-[11px] text-foreground-500">{t("licenses.plannerSub")}</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-background-100 px-3 py-1 text-[11px] font-medium text-foreground-700">
            <span className="flex h-3 w-3 items-center justify-center">
              <i className="ri-key-2-line" />
            </span>
            {t("licenses.plannerCount", { count: total })}
          </span>
          <button
            type="button"
            disabled={renewable.length === 0}
            onClick={handleExport}
            title={
              renewable.length === 0
                ? t("licenses.plannerEmpty")
                : t("licenses.plannerExportTitle")
            }
            className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-primary-300 bg-primary-50 px-3 py-1.5 text-xs font-medium text-primary-700 transition-colors hover:bg-primary-100 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <span className="flex h-3.5 w-3.5 items-center justify-center text-primary-700">
              <i className={exporting ? "ri-check-line" : "ri-download-2-line"} />
            </span>
            {exporting ? t("licenses.plannerExported") : t("licenses.plannerExport")}
          </button>
        </div>
      </div>

      {total === 0 ? (
        <div className="mt-4 flex flex-col items-center gap-2 rounded-md border border-background-200 bg-background-100 py-8 text-center">
          <span className="flex h-9 w-9 items-center justify-center rounded-md bg-background-200 text-foreground-500">
            <i className="ri-calendar-line text-lg" />
          </span>
          <p className="text-xs font-medium text-foreground-700">{t("licenses.plannerEmpty")}</p>
          <p className="max-w-xs text-[11px] text-foreground-400">{t("licenses.plannerEmptyDesc")}</p>
        </div>
      ) : (
        <>
          <div className="mt-4 grid grid-cols-2 gap-2.5 sm:grid-cols-3 lg:grid-cols-5">
            {BUCKETS.map((bucket) => {
              const count = grouped[bucket.key].length;
              const isActive = active === bucket.key;
              const disabled = count === 0;
              return (
                <button
                  key={bucket.key}
                  type="button"
                  disabled={disabled}
                  onClick={() => setActive(isActive ? null : bucket.key)}
                  aria-pressed={isActive}
                  className={`flex cursor-pointer flex-col gap-1.5 rounded-md border p-3 text-left transition-colors ${
                    isActive
                      ? "border-primary-300 bg-primary-50"
                      : "border-background-200 bg-background-50 hover:border-primary-200 hover:bg-background-100"
                  } ${disabled ? "cursor-not-allowed opacity-55 hover:border-background-200 hover:bg-background-50" : ""}`}
                >
                  <span className={`flex h-7 w-7 items-center justify-center rounded-md ${bucket.chip}`}>
                    <i className={`${bucket.icon} text-sm`} />
                  </span>
                  <span className="text-[11px] text-foreground-600">{t(bucket.labelKey)}</span>
                  <span className="font-heading text-xl font-semibold text-foreground-950">{count}</span>
                </button>
              );
            })}
          </div>

          {active && activeDef && (
            <div className="mt-4 border-t border-background-200 pt-4">
              <p className="flex items-center gap-1.5 text-xs font-medium text-foreground-700">
                <span className={`flex h-5 w-5 items-center justify-center rounded ${activeDef.chip}`}>
                  <i className={`${activeDef.icon} text-[11px]`} />
                </span>
                {t(activeDef.labelKey)}
                <span className="text-foreground-400">· {t("licenses.plannerCount", { count: activeList.length })}</span>
              </p>
              <ul className="mt-3 flex flex-col gap-2">
                {activeList.map((license) => {
                  const days = license.expires_at ? daysUntil(license.expires_at) : null;
                  const hint =
                    days === null
                      ? t("licenses.permanent")
                      : days < 0
                        ? t("licenses.plannerOverdueDays", { days: Math.abs(days) })
                        : t("licenses.plannerDaysLeft", { days });
                  const urgent = active === "overdue" || active === "urgent";
                  return (
                    <li
                      key={license.id}
                      className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-background-200 bg-background-100 px-3.5 py-2.5"
                    >
                      <div className="flex min-w-0 items-center gap-2.5">
                        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-primary-100 text-primary-700">
                          <i className="ri-key-2-line text-sm" />
                        </span>
                        <div className="min-w-0">
                          <p className="truncate text-sm font-medium text-foreground-950">{license.product_name}</p>
                          <div className="mt-0.5 flex flex-wrap items-center gap-x-2.5 gap-y-0.5 text-[11px] text-foreground-500">
                            <span className="rounded bg-background-200 px-1.5 py-0.5 text-foreground-700">
                              {edition(license.edition)}
                            </span>
                            <span className="font-mono">LH-••••-{license.license_key.slice(-4)}</span>
                            <span>
                              {license.expires_at ? formatFullDate(license.expires_at) : t("licenses.permanent")}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span
                          className={`inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2.5 py-1 text-[11px] font-medium ${
                            urgent ? "bg-accent-100 text-accent-800" : "bg-secondary-100 text-secondary-900"
                          }`}
                        >
                          <span className="flex h-3 w-3 items-center justify-center">
                            <i className="ri-time-line" />
                          </span>
                          {hint}
                        </span>
                        <StatusBadge meta={statusMeta(license.status)} />
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </>
      )}
    </div>
  );
}